import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  REGIONS_CREATE,
  REGIONS_UPDATE,
  REGIONS_VIEW,
} from "../../organizationPermissions";
import { archivedRegionsSearchSchema, regionsSearchSchema } from "./searchSchemas";
import type { OrganizationRouteFactory } from "./organizationRouteContext";

const RegionsListScreen = lazyRouteComponent(
  () => import("../RegionsListScreen"),
  "RegionsListScreen",
);
const ArchivedRegionsListScreen = lazyRouteComponent(
  () => import("../ArchivedRegionsListScreen"),
  "ArchivedRegionsListScreen",
);
const RegionFormScreen = lazyRouteComponent(
  () => import("../RegionFormScreen"),
  "RegionFormScreen",
);
const RegionDetailScreen = lazyRouteComponent(
  () => import("../RegionDetailScreen"),
  "RegionDetailScreen",
);

export const createRegionsRoutes: OrganizationRouteFactory = ({
  context,
  regions,
}) => {
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/regions/$id/edit`,
    beforeLoad: requirePermission([REGIONS_UPDATE]),
    component: function RegionEditRouteScreen() {
      // See createCompanyDetailRoutes' identical comment: route-scoped
      // params, not a manual pathname split, so this never renders with a
      // stale id during the navigate-away transition after a save.
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(RegionFormScreen, {
        repository: regions,
        basePath: context.basePath,
        regionId: id,
      });
    },
  });

  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/regions/$id`,
    beforeLoad: requirePermission([REGIONS_VIEW]),
    component: function RegionDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(RegionDetailScreen, {
        repository: regions,
        basePath: context.basePath,
        regionId: id,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/regions`,
      validateSearch: regionsSearchSchema,
      beforeLoad: requirePermission([REGIONS_VIEW]),
      component: () =>
        createElement(RegionsListScreen, {
          repository: regions,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/regions/create`,
      beforeLoad: requirePermission([REGIONS_CREATE]),
      component: () =>
        createElement(RegionFormScreen, {
          repository: regions,
          basePath: context.basePath,
        }),
    }),
    // Registered before `$id`/`$id/edit` -- "archived"/"create" are literal
    // segments, not ids, same convention as createBranchesRoutes.
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/regions/archived`,
      validateSearch: archivedRegionsSearchSchema,
      beforeLoad: requirePermission([REGIONS_VIEW]),
      component: () =>
        createElement(ArchivedRegionsListScreen, {
          repository: regions,
          basePath: context.basePath,
        }),
    }),
    editRoute,
    detailRoute,
  ];
};
